import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import Axios from "axios";

import Config from "../../Config";
import { get_paymentId } from "../../../store/Actions/authActions";
import {
  formatOrderDate,
  nextMilestoneTs,
  normalizeOrder,
  parseOrderDate,
  sortByNextMilestone,
} from "../../../utils/orders";
import WalletTopup from "../Modals/WalletTopup";
import ConfirmDialog from "./ConfirmDialog";
import { editFailed, startOrderEdit } from "./editOrder";
import { Icon, Sprite } from "./icons";
import OrderBlock from "./OrderBlock";
import { pastOrderId } from "../../../utils/pastOrders";
import { openReceipt, reorder } from "./pastApi";
import PastOrders from "./PastOrders";
import "./orders.css";

/**
 * « Mes commandes » — design « Mes commandes - En cours.dc.html ».
 *
 * Onglet En cours : une commande à la fois, triées par prochaine étape, avec
 * une barre de pagination quand il y en a plusieurs. Onglet Passées : les
 * cartes de « Mes commandes - Passees et detail », qui ouvrent le détail
 * (/orders/:id, OrderDetail).
 *
 * GET /customer/orders est relu au retour sur l'onglet quand la dernière
 * lecture a plus de 30 s, comme la requête « orders » du mobile.
 */

const STALE_MS = 30000;

const authHeaders = (authUser) => ({
  uid: authUser?.user_uid,
  client: authUser?.user_client,
  "access-token": authUser?.user_access_token,
});

export function OrdersPage({
  authUser,
  homeAddresses,
  officeAddresses,
  cards,
  dispatch,
  history,
  location,
  now,
}) {
  const { t, i18n } = useTranslation("app");
  // Les dates suivent la langue réellement affichée (codes « fn » / « du »).
  const lang = i18n.language === "fn" ? "fr" : i18n.language === "du" ? "nl" : "en";
  const tab = new URLSearchParams(location?.search).get("tab") === "past" ? "past" : "current";

  const [status, setStatus] = useState("loading");
  const [current, setCurrent] = useState([]);
  const [past, setPast] = useState([]);
  const [index, setIndex] = useState(0);
  const [laundryName, setLaundryName] = useState(undefined);
  const [walletBalance, setWalletBalance] = useState(0);
  const [topup, setTopup] = useState(null);
  const [editing, setEditing] = useState(false);
  const fetchedAt = useRef(0);

  const loadOrders = useCallback(() => {
    fetchedAt.current = Date.now();
    return Axios({ url: `${Config.apiURL}/customer/orders`, method: "GET", headers: authHeaders(authUser) })
      .then((response) => {
        setCurrent(sortByNextMilestone((response?.data?.current_orders || []).map(normalizeOrder)));
        setPast((response?.data?.past_orders || []).map(normalizeOrder));
        setStatus("ready");
      })
      .catch(() => {
        // Une relecture ratée garde la liste déjà affichée.
        setStatus((previous) => (previous === "ready" ? previous : "error"));
      });
  }, [authUser]);

  useEffect(() => {
    loadOrders();

    Axios({ url: `${Config.apiURL}/customer/laundry_partner`, method: "GET", headers: authHeaders(authUser) })
      .then((response) => {
        const data = response?.data;
        const partner = data?.laundry_partner ?? data?.partner ?? data?.data ?? data;
        setLaundryName(partner?.name || undefined);
      })
      .catch(() => setLaundryName(undefined));

    const refresh = () => {
      if (!document.hidden && Date.now() - fetchedAt.current > STALE_MS) loadOrders();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [authUser, loadOrders]);

  /* Le portefeuille et les cartes ne servent qu'aux paiements échoués. */
  const hasPaymentIssue = current.some((order) => order.payment_status === "failed");
  useEffect(() => {
    if (!hasPaymentIssue) return;
    Axios({ url: `${Config.apiURL}/customer/wallet`, method: "GET", headers: authHeaders(authUser) })
      .then((response) => setWalletBalance(Number(response?.data?.wallet?.amount) || 0))
      .catch(() => setWalletBalance(0));
    dispatch(get_paymentId());
  }, [hasPaymentIssue, authUser, dispatch]);

  // La liste peut raccourcir entre deux lectures (une commande livrée).
  const safeIndex = Math.min(index, Math.max(current.length - 1, 0));
  const order = current[safeIndex];

  const openTab = (next) => history.push(next === "past" ? "/orders?tab=past" : "/orders");

  const [editError, setEditError] = useState(null);

  const edit = async (target, issue = null) => {
    if (editing) return;
    setEditing(true);
    try {
      history.push(await dispatch(startOrderEdit(target, issue)));
    } catch (error) {
      setEditing(false);
      setEditError(editFailed(t));
    }
  };

  const title =
    tab === "past"
      ? t("orders.pastOrders")
      : status === "ready" && current.length === 1
      ? t("orders.currentOrder")
      : t("orders.currentOrders");

  return (
    <div className="wosh-ord">
      <Sprite />
      <div className="wosh-ord__page">
        <header className="wosh-ord__head">
          <div className="wosh-ord__titles">
            <div className="wosh-ord__eyebrow">{t("orders.headerEyebrow")}</div>
            <h1 className="wosh-ord__title">{title}</h1>
          </div>
          <div className="wosh-ord__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "current"}
              className={`wosh-ord__tab${tab === "current" ? " wosh-ord__tab--on" : ""}`}
              onClick={() => openTab("current")}
            >
              {t("orders.currentTab")}
              {current.length > 0 && <span className="wosh-ord__count">{current.length}</span>}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "past"}
              className={`wosh-ord__tab${tab === "past" ? " wosh-ord__tab--on" : ""}`}
              onClick={() => openTab("past")}
            >
              {t("orders.pastTab")}
            </button>
          </div>
        </header>

        {status === "loading" ? (
          <div className="wosh-ord__center" aria-busy="true">
            <span className="wosh-ord__spinner" />
          </div>
        ) : status === "error" ? (
          <div className="wosh-ord__error">
            <div className="wosh-ord__error-icon">
              <Icon name="cloud-offline-outline" />
            </div>
            <div className="wosh-ord__error-title">{t("orders.cantLoadOrders")}</div>
            <div className="wosh-ord__error-sub">{t("orders.cantLoadSub")}</div>
          </div>
        ) : tab === "past" ? (
          <PastOrders
            orders={past}
            t={t}
            lang={lang}
            now={now}
            onOpen={(target) => history.push(`/orders/${target.id}`)}
            onReorder={(target) => dispatch(reorder(target, authUser))}
            onOpenCart={() => history.push("/home")}
            onReceipt={(target) => openReceipt(target.id, authUser)}
            onPeppol={(target) =>
              history.push(`/orders/${target.id}/invoice`, {
                token: target.secure_token,
                woshId: pastOrderId(target),
              })
            }
          />
        ) : !order ? (
          <div className="wosh-ord__empty">
            <div className="wosh-ord__empty-icon">
              <Icon name="basket-outline" />
            </div>
            <div className="wosh-ord__empty-title">{t("orders.noActiveOrders")}</div>
            <div className="wosh-ord__empty-sub">{t("orders.noActiveOrdersSub")}</div>
            <button type="button" className="wosh-ord__cta" onClick={() => history.push("/home")}>
              {t("orders.startOrder")}
            </button>
          </div>
        ) : (
          <div className="wosh-ord__stack">
            {current.length > 1 && (
              <Pager
                order={order}
                index={safeIndex}
                total={current.length}
                onMove={setIndex}
                t={t}
                lang={lang}
              />
            )}
            <OrderBlock
              key={order.id}
              order={order}
              t={t}
              lang={lang}
              laundryName={laundryName}
              authUser={authUser}
              homeAddresses={homeAddresses}
              officeAddresses={officeAddresses}
              walletBalance={walletBalance}
              cards={cards}
              onEdit={(target) => edit(target)}
              onResolve={(target, leg) => edit(target, leg)}
              onTryAnotherCard={() => history.push("/paymentMethod")}
              onTopup={(target, info) => setTopup({ total: info.total, balance: info.balance })}
            />
            {current.length > 1 && (
              <div className="wosh-ord__dots">
                {current.map((item, i) => (
                  <button
                    type="button"
                    key={item.id}
                    aria-label={t("orders.counter", { i: i + 1, total: current.length })}
                    className={`wosh-ord__dot${i === safeIndex ? " wosh-ord__dot--on" : ""}`}
                    onClick={() => setIndex(i)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <WalletTopup
        open={Boolean(topup)}
        onClose={() => setTopup(null)}
        total={topup?.total}
        balance={topup?.balance}
      />

      <ConfirmDialog
        open={Boolean(editError)}
        {...editError}
        confirm="OK"
        onCancel={() => setEditError(null)}
        onConfirm={() => setEditError(null)}
      />
    </div>
  );
}

/** « 1 / 2 · WOSH-4830 · Collecte jeu. 24 sept. », flèches précédente / suivante. */
function Pager({ order, index, total, onMove, t, lang }) {
  const next = nextMilestoneTs(order);
  const pickup = parseOrderDate(order.pickup_date);
  const isPickup = pickup && pickup.getTime() === next;
  const date = isPickup ? order.pickup_date : order.delivery_date;
  const milestone = formatOrderDate(date, lang)
    ? `${t(isPickup ? "checkout.pickup" : "checkout.delivery")} ${formatOrderDate(date, lang)}`
    : "";

  return (
    <div className="wosh-ord__pager">
      <div className="wosh-ord__pager-info">
        <span className="wosh-ord__pager-counter">{t("orders.counter", { i: index + 1, total })}</span>
        <span className="wosh-ord__pager-sep" />
        {order.wosh_order_id && <span className="wosh-ord__pager-id">{order.wosh_order_id}</span>}
        {milestone && <span className="wosh-ord__pager-when">{milestone}</span>}
      </div>
      <div className="wosh-ord__pager-nav">
        <button
          type="button"
          className="wosh-ord__pager-btn"
          disabled={index === 0}
          onClick={() => onMove(index - 1)}
          aria-label="←"
        >
          <Icon name="arrow-back" />
        </button>
        <button
          type="button"
          className="wosh-ord__pager-btn"
          disabled={index === total - 1}
          onClick={() => onMove(index + 1)}
          aria-label="→"
        >
          <Icon name="arrow-forward" />
        </button>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
  homeAddresses: state?.auth?.homeAddress,
  officeAddresses: state?.auth?.officeAddress,
  cards: state?.auth?.paymentId,
});

export default connect(mapStateToProps)(OrdersPage);
